/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { object, func } from 'prop-types';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ReactMarkdown from 'react-markdown';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { trackEvent as track } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles((theme) => ({
    sectionHeader: {
        fontSize: '22px',
        margin: '0 auto',
        maxWidth: '980px',
    },
    couponArea: (panelBackgroundColor) => ({
        margin: '0 auto 25px',
        backgroundColor: panelBackgroundColor?.color || theme?.palette?.cms?.['Light Beige'] || '#F8F8F0',
        border: `2px dashed ${theme?.palette?.cms?.dimSilver || '#ccc'}`,
        maxWidth: '980px',
    }),
    noCoupons: {
        textAlign: 'center',
        fontSize: '22px',
        margin: '30px auto',
    },
    couponSingle: {
        padding: '20px 30px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        width: '100%',
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        transition: 'all 0.3s',
        position: 'relative',
        minHeight: '145px',
    },

    iconContainer: {
        width: '120px',
    },
    icon: {
        height: '100px',
    },
    couponHeadingContainer: {
        marginTop: '0',
        marginBottom: '0',
    },
    couponHeading: {
        '& h3': {
            fontSize: '28px',
            marginTop: '0',
            marginBottom: '0',
        },
        '& p': {
            marginTop: '0',
            marginBlockEnd: '5px',
        },
        '& strong': {
            color: theme?.palette?.cms?.['CTA Red'] || '#A1001A',
        },
    },
    copyContainer: {
        margin: '0 20px',
    },
    promoCopy: {
        '& p': {
            marginTop: '5px',
            fontSize: '18px',
        },
        '& h3': {
            margin: '5px auto',
        },
        '& strong': {
            color: theme?.palette?.cms?.['CTA Red'] || '#A1001A',
        },
    },
    detailsLink: {
        textDecoration: 'underline',
        display: 'inline-block',
        color: theme?.palette?.cms?.tertiary_hover || '#666',
        cursor: 'pointer',
        minWidth: '135px',
        padding: '3px',
        transition: 'all .3',
        '&:focus': {
            outline: 'none',
        },
    },
    ctaBtn: {
        backgroundColor: theme?.palette?.cms?.['CTA Red'] || '#A1001A',
        color: 'white',
        marginRight: '5px',
        marginBottom: '5px',
        padding: '5px 10px',
        borderRadius: '0',
        fontWeight: '400',
        fontSize: '16px',
        textDecoration: 'none',
        textTransform: 'Uppercase',
        display: 'flex',
        lineHeight: '1.75em',
        alignItems: 'center',
        transition: 'all 0.3s',
        minWidth: '200px',
        width: '100%',
        justifyContent: 'center',
        textAlign: 'center',
        flexWrap: 'wrap',
        '&:hover': {
            backgroundColor: `${theme?.palette?.cms?.primary_hover}`,
        },
    },
    codeCopiedMsg: {
        color: theme?.palette?.cms?.success || '#70b603',
    },
    ieCodeBox: {
        fontSize: '22px',
        width: '200px',
    },
    ieCode: {
        color: theme?.palette?.cms?.success || '#70b603',
        fontWeight: '700',
    },
    restrictionsShown: (panelBackgroundColor) => ({
        margin: '0 auto',
        fontSize: '13px',
        padding: '5px 10px',
        backgroundColor: panelBackgroundColor?.color || theme?.palette?.cms?.['Light Beige'] || '#F8F8F0',
        '& p': {
            marginTop: '0',
        },
        /* unshrink, then fade in */
        transition: 'font-size .25s ease, margin .25s ease, padding .25s ease, opacity .5s  ease .25s',
    }),
    restrictionsHidden: (panelBackgroundColor) => ({
        fontSize: '0',
        margin: '0 auto',
        padding: '0 10px',
        opacity: '0',
        backgroundColor: panelBackgroundColor?.color || theme?.palette?.cms?.['Light Beige'] || '#F8F8F0',
        '& p': {
            marginTop: '0',
        },
        /* fade out, then shrink */
        transition: 'opacity ease .25s, fontSize .5s ease .25s, margin .5s ease .25s, padding .5s ease .25s',
    }),
}));

const CouponPageUIDesktop = ({ data, panelBackgroundColor, trackEvent }) => {
    const classes = useStyles(panelBackgroundColor);

    const couponTemplate = (couponData) => {
        const [showDetails, setShowDetails] = React.useState(false);
        const onToggleDetails = () => setShowDetails(!showDetails);
        const noop = () => {};
        const [codeCopied, setCodeCopied] = React.useState(false);

        const copyCode = () => {
            if (navigator?.clipboard) {
                navigator.clipboard.writeText(couponData.promotion_code);
                return true;
            }
            return noop;
        };

        const trackFunc = (action, label) => {
            trackEvent({
                eventCategory: 'Coupon Page',
                eventAction: action || 'click',
                eventLabel: label || 'coupon page element',
            });
        };

        const toggleDetails = (code) => {
            onToggleDetails();
            if (!showDetails) {
                trackFunc('View', `Details for ${code}`);
            } else {
                trackFunc('Hide', `Details for ${code}`);
            }
        };

        const codeCopy = (code) => {
            copyCode();
            setCodeCopied(code);
            trackFunc('Code Copy', code);
        };

        return (
            <div className={classes.couponArea} key={couponData.title}>
                <div className={classes.couponSingle}>
                    <div className={classes.iconContainer}>
                        <img className={classes.icon} src={couponData.coupon_page?.coupon_icon?.url} alt="coupon-icon" />
                    </div>
                    <div className={classes.copyContainer}>
                        {
                            couponData.coupon_page.heading &&  (
                                <h3 className={classes.couponHeadingContainer}>
                                    <ReactMarkdown className={classes.couponHeading} escapeHtml={false} source={couponData.coupon_page?.heading} />
                                </h3>
                            )
                        }
                        { couponData.coupon_page.promo_copy && <ReactMarkdown className={classes.promoCopy} escapeHtml={false} source={couponData.coupon_page?.promo_copy} /> }
                        { couponData.promotion_details && (
                            <div
                                className={classes.detailsLink}
                                role="button"
                                onClick={() => toggleDetails(couponData.promotion_code)}
                                onKeyDown={() => toggleDetails(couponData.promotion_code)}
                                tabIndex={0}
                                data-testid="viewDetails"
                            >
                                {showDetails ? 'Hide Terms' : 'Restrictions Apply'}
                            </div>
                        )}
                    </div>
                    <div className={classes.ctaContainer}>
                        {/* internal CTA */}
                        { ((couponData.url && !couponData.promotion_code) || (couponData.promotion_code && codeCopied)) && (
                            <Link to={couponData.url} color="primary" data-testid="shopNowCTA" className={classes.ctaBtn} aria-label={couponData?.coupon_page?.heading || 'Coupons'} onClick={() => { trackFunc('Shop Now CTA', couponData.url); }}>
                                {(couponData.promotion_code && codeCopied) ? 'Shop Now' : couponData.coupon_page.cta_text}
                            </Link>
                        )}

                        {/* external Link */}
                        { couponData.external_link && (
                            <a href={couponData.external_link} color="primary" className={classes.ctaBtn} onClick={() => { trackFunc('Shop Now CTA - External', couponData.url); }}>
                                {couponData.coupon_page.cta_text}
                            </a>
                        )}

                        {/* coupon code */}
                        { (couponData.promotion_code && !codeCopied) && (

                            <Button
                                color="primary"
                                className={classes.ctaBtn}
                                onClick={() => { codeCopy(couponData.promotion_code); }}
                                data-testid="copyCodeBtn"
                            >
                                Copy Code&nbsp;<b>{couponData.promotion_code}</b>
                            </Button>

                        )}
                        <div className={classes.codeCopiedMsg}>
                            {codeCopied && <span data-testid="codeCopiedConfirmation">Code &quot;{couponData.promotion_code}&quot; Copied!</span>}
                        </div>
                    </div>
                </div>
                <div className={showDetails ? classes.restrictionsShown : classes.restrictionsHidden}>{ showDetails && <ReactMarkdown>{couponData.promotion_details}</ReactMarkdown> }</div>
            </div>
        );
    };

    const limitedTimeCoupons = data.coupons.tempPromos.map((coupon) => couponTemplate(coupon));
    const everydayDeals = data.coupons.persistentPromos.map((coupon) => couponTemplate(coupon));

    if (!data || !data.coupons.tempPromos || !Array.isArray(data.coupons.tempPromos) || !data.coupons.persistentPromos || !Array.isArray(data.coupons.persistentPromos)) {
        return null;
    }

    return (
        <Grid xs={12} md={9} item>
            {limitedTimeCoupons.length > 0 && <h2 className={classes.sectionHeader}>Limited Time Offers</h2>}
            {limitedTimeCoupons}
            {everydayDeals.length > 0 && <h2 className={classes.sectionHeader}>Everyday Deals</h2>}
            {everydayDeals}

            {/* No Coupons Fallback */}
            {limitedTimeCoupons.length === 0 && everydayDeals.length === 0
                && <div className={classes.noCoupons} xs={12} md={12} item>We have no coupons at this time.<br />Please check back later.</div>}
        </Grid>
    );
};

CouponPageUIDesktop.propTypes = {
    data: object.isRequired,
    panelBackgroundColor: object,
    trackEvent: func.isRequired,
};
CouponPageUIDesktop.defaultProps = {
    panelBackgroundColor: { backgroundColor: '#fff' },
};
const mapDispatchToProps = (dispatch) => ({
    panelBackgroundColor: { backgroundColor: '#fff' },
    trackEvent: bindActionCreators(track, dispatch),
});

export default connect(
    null,
    mapDispatchToProps,
)(CouponPageUIDesktop);
